import { Command } from 'commander';
import { WorkflowManager } from '../core/workflow-manager';
import { logger } from '../utils/logger';
import Table from 'cli-table3';
import chalk from 'chalk';
import inquirer from 'inquirer';
import path from 'path';

export function createHooksCommand(): Command {
  const command = new Command('hooks');
  
  command
    .description('管理工作流 hooks')
    .addCommand(createListCommand())
    .addCommand(createEnableCommand())
    .addCommand(createDisableCommand())
    .addCommand(createCreateCommand())
    .addCommand(createTestCommand());
  
  return command;
}

function createListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('列出所有可用的 hooks')
    .option('-t, --type <type>', '按类型筛选')
    .action(async (options) => {
      try {
        const manager = new WorkflowManager();
        await manager.initialize();
        
        const hookManager = manager.getHookManager();
        const hookRegistry = manager.getHookRegistry();
        
        // 获取所有 hooks
        const hooks = await hookManager.listHooks();
        const builtinHooks = hookRegistry.getBuiltinHooks();
        
        // 创建表格
        const table = new Table({
          head: ['类型', '名称', '类型', '状态', '优先级', '描述'],
          style: { head: ['cyan'] }
        });
        
        // 显示内置 hooks
        console.log(chalk.bold('\n📌 内置 Hooks:'));
        for (const hook of builtinHooks) {
          if (!options.type || hook.type.includes(options.type)) {
            table.push([
              chalk.blue(hook.type),
              hook.name,
              'builtin',
              hook.enabled ? chalk.green('✓ 启用') : chalk.gray('✗ 禁用'),
              hook.priority || 0,
              hook.description
            ]);
          }
        }
        
        // 显示用户 hooks
        console.log(table.toString());
        
        if (Object.keys(hooks).length > 0) {
          console.log(chalk.bold('\n📂 用户 Hooks:'));
          const userTable = new Table({
            head: ['类型', '名称', '类型', '路径'],
            style: { head: ['cyan'] }
          });
          
          for (const [type, handlers] of Object.entries(hooks)) {
            if (!options.type || type.includes(options.type)) {
              for (const handler of handlers) {
                if (handler.type === 'script') {
                  userTable.push([
                    chalk.blue(type),
                    handler.name,
                    'script',
                    handler.path || ''
                  ]);
                }
              }
            }
          }
          
          console.log(userTable.toString());
        }
        
        // 显示可用的 hook 类型
        console.log(chalk.bold('\n🎯 可用的 Hook 类型:'));
        const types = [
          'task:*', 'prd:*', 'design:*', 'workflow:*', 
          'file:*', 'tool:*', 'ai:*', 'session:*'
        ];
        console.log(types.map(t => chalk.gray(`  - ${t}`)).join('\n'));
        
      } catch (error: any) {
        logger.error('列出 hooks 失败:', error.message);
      }
    });
  
  return command;
}

function createEnableCommand(): Command {
  const command = new Command('enable');
  
  command
    .description('启用 hook')
    .argument('<name>', 'Hook 名称')
    .action(async (name) => {
      try {
        const manager = new WorkflowManager();
        await manager.initialize();
        
        const hookRegistry = manager.getHookRegistry();
        
        // 尝试启用内置 hook
        await hookRegistry.toggleBuiltinHook(name, true);
        
        logger.success(`Hook ${name} 已启用`);
        
      } catch (error: any) {
        logger.error('启用 hook 失败:', error.message);
      }
    });
  
  return command;
}

function createDisableCommand(): Command {
  const command = new Command('disable');
  
  command
    .description('禁用 hook')
    .argument('<name>', 'Hook 名称')
    .action(async (name) => {
      try {
        const manager = new WorkflowManager();
        await manager.initialize();
        
        const hookRegistry = manager.getHookRegistry();
        
        // 尝试禁用内置 hook
        await hookRegistry.toggleBuiltinHook(name, false);
        
        logger.success(`Hook ${name} 已禁用`);
        
      } catch (error: any) {
        logger.error('禁用 hook 失败:', error.message);
      }
    });
  
  return command;
}

function createCreateCommand(): Command {
  const command = new Command('create');
  
  command
    .description('创建新的 hook 脚本')
    .argument('[type]', 'Hook 类型 (如 task:complete)')
    .option('-l, --language <lang>', '脚本语言 (bash/javascript)', 'bash')
    .action(async (type, options) => {
      try {
        const manager = new WorkflowManager();
        await manager.initialize();
        
        const hookRegistry = manager.getHookRegistry();
        
        // 如果没有提供类型，交互式选择
        if (!type) {
          const hookTypes = [
            'task:create', 'task:update', 'task:complete', 'task:delete',
            'prd:create', 'design:create', 'design:approve',
            'file:change', 'workflow:start', 'workflow:complete'
          ];
          
          const { selectedType } = await inquirer.prompt([
            {
              type: 'list',
              name: 'selectedType',
              message: '选择 hook 类型:',
              choices: hookTypes
            }
          ]);
          
          type = selectedType;
        }
        
        // 生成文件名
        const category = type.split(':')[0];
        const action = type.split(':')[1];
        const ext = options.language === 'javascript' ? 'js' : 'sh';
        const filename = `on-${action}.${ext}`;
        const scriptPath = path.join(process.cwd(), '.workflow/hooks', category, filename);
        
        // 创建模板
        await hookRegistry.createHookTemplate(type, scriptPath, options.language);
        
        console.log(chalk.green('\n✅ Hook 脚本已创建:'), scriptPath);
        console.log('\n下一步:');
        console.log('1. 编辑脚本: ' + chalk.cyan(`${process.env.EDITOR || 'code'} ${scriptPath}`));
        console.log('2. 测试 hook: ' + chalk.cyan(`workflow hooks test ${type}`));
        
      } catch (error: any) {
        logger.error('创建 hook 失败:', error.message);
      }
    });
  
  return command;
}

function createTestCommand(): Command {
  const command = new Command('test');
  
  command
    .description('测试 hook')
    .argument('<type>', 'Hook 类型')
    .option('-d, --data <json>', '测试数据 (JSON 格式)', '{}')
    .action(async (type, options) => {
      try {
        const manager = new WorkflowManager();
        await manager.initialize();
        
        const hookManager = manager.getHookManager();
        
        // 解析测试数据
        let testData;
        try {
          testData = JSON.parse(options.data);
        } catch (e) {
          testData = { message: options.data };
        }
        
        console.log(chalk.bold('\n🧪 测试 Hook:'), type);
        console.log(chalk.gray('测试数据:'), JSON.stringify(testData, null, 2));
        console.log();
        
        // 触发 hook
        const startTime = Date.now();
        await hookManager.trigger(type, testData, {
          source: 'test',
          timeout: 10000
        });
        
        const duration = Date.now() - startTime;
        console.log(chalk.green(`\n✅ Hook 执行完成 (${duration}ms)`));
        
      } catch (error: any) {
        logger.error('测试 hook 失败:', error.message);
      }
    });
  
  return command;
}